import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  CalendarDays, Plus, X, Check, Ban, Loader2, Inbox, AlertTriangle, Info, UserCheck,
} from 'lucide-react';
import { api } from '@/api/client';

type Leave = {
  id: number;
  employeeName?: string | null;
  empCode?: string | null;
  department?: string | null;
  leaveType: string;
  fromDate: string;
  toDate: string;
  days: string | number;
  isHalfDay: boolean;
  halfDaySession?: string | null;
  reason: string;
  contactDuringLeave?: string | null;
  handoverTo?: string | null;
  status: string;
  decisionRemarks?: string | null;
  decidedByName?: string | null;
  decidedAt?: string | null;
  reportingManagerName?: string | null;
  currentApproverName?: string | null;
  createdAt: string;
};

type LeaveLink = {
  linked: boolean;
  employeeName?: string;
  empCode?: string;
  hasManager?: boolean;
  reportingManagerName?: string | null;
  leaveTypes: string[];
};

const STATUS_CHIP: Record<string, string> = {
  Pending: 'bg-amber-100 text-amber-700 border-amber-200',
  Approved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  Rejected: 'bg-rose-100 text-rose-700 border-rose-200',
  Cancelled: 'bg-slate-100 text-slate-500 border-slate-200',
};

/** Date only — a leave is a calendar day, so never show a time against it. */
function day(d?: string | null) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { timeZone: 'UTC', day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
}
function range(l: Leave) {
  const same = l.fromDate.slice(0, 10) === l.toDate.slice(0, 10);
  if (same) return day(l.fromDate) + (l.isHalfDay ? ` · ${l.halfDaySession ?? 'First'} half` : '');
  return `${day(l.fromDate)} → ${day(l.toDate)}`;
}
/** Today as YYYY-MM-DD in IST — the default the form opens on. */
function todayIso() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
}

export function LeaveRequestsPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<'mine' | 'approvals'>('mine');
  const [formOpen, setFormOpen] = useState(false);

  const { data: link } = useQuery<LeaveLink>({
    queryKey: ['me', 'leave-link'],
    queryFn: async () => (await api.get('/me/leave/link')).data,
    staleTime: 5 * 60 * 1000, retry: false,
  });
  const { data: mine = [], isLoading: loadingMine } = useQuery<Leave[]>({
    queryKey: ['me', 'leave'],
    queryFn: async () => (await api.get('/me/leave')).data,
  });
  const { data: approvals = [] } = useQuery<Leave[]>({
    queryKey: ['me', 'leave-approvals'],
    queryFn: async () => (await api.get('/me/leave-approvals')).data,
    staleTime: 30 * 1000,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['me', 'leave'] });
    qc.invalidateQueries({ queryKey: ['me', 'leave-approvals'] });
  };

  const cancel = useMutation({
    mutationFn: async (id: number) => (await api.post(`/me/leave/${id}/cancel`)).data,
    onSuccess: () => { toast.success('Leave withdrawn'); refresh(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not withdraw this leave'),
  });
  const decide = useMutation({
    mutationFn: async ({ id, action, remarks }: { id: number; action: 'approve' | 'reject'; remarks?: string }) =>
      (await api.post(`/me/leave-approvals/${id}/${action}`, { remarks })).data,
    onSuccess: (_d, v) => { toast.success(v.action === 'approve' ? 'Leave approved' : 'Leave rejected'); refresh(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not record that decision'),
  });

  // An employee with nobody reporting to them has no approvals to work, so the tab is not offered —
  // an empty second tab reads as "broken", not as "nothing for you".
  const showApprovals = approvals.length > 0;

  return (
    <div className="p-5 max-w-5xl mx-auto">
      <div className="flex items-start justify-between mb-1 gap-3">
        <div>
          <div className="flex items-center gap-2">
            <CalendarDays className="text-primary" size={22} />
            <h1 className="text-xl font-bold">Leave</h1>
          </div>
          <p className="text-sm text-slate-500 mt-0.5">
            Apply for leave and track its approval. Applications go to your Team Manager.
          </p>
        </div>
        <button onClick={() => setFormOpen(true)}
          className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium flex items-center gap-1.5 hover:bg-primary/90 shrink-0">
          <Plus size={15} /> Apply for Leave
        </button>
      </div>

      {/* The two ways this can be unusable are worth saying out loud rather than failing on submit. */}
      {link && !link.linked && (
        <div className="mt-3 flex items-start gap-2 text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-3">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>Your login isn’t linked to an employee record yet, so you can’t apply for leave. Ask HR to add your mobile number to your employee profile — that’s what links the two.</span>
        </div>
      )}
      {link?.linked && !link.hasManager && (
        <div className="mt-3 flex items-start gap-2 text-sm bg-sky-50 border border-sky-200 text-sky-800 rounded-lg p-3">
          <Info size={16} className="mt-0.5 shrink-0" />
          <span>No Team Manager is set on your employee profile, so your leave will go to the company approver instead. Ask HR to set your reporting manager.</span>
        </div>
      )}
      {link?.linked && link.hasManager && (
        <p className="mt-3 text-xs text-slate-500 flex items-center gap-1.5">
          <UserCheck size={13} /> Your leave is approved by <b className="text-slate-700">{link.reportingManagerName}</b>.
        </p>
      )}

      <div className="flex gap-1 border-b border-slate-200 mb-4 mt-4">
        <TabBtn active={tab === 'mine'} onClick={() => setTab('mine')} label="My Leave" count={mine.length} />
        {showApprovals && (
          <TabBtn active={tab === 'approvals'} onClick={() => setTab('approvals')} label="Approvals" count={approvals.length} highlight />
        )}
      </div>

      {tab === 'mine' && (
        loadingMine ? <Loading /> :
        mine.length === 0 ? <Empty text="You haven’t applied for any leave yet." /> : (
          <div className="space-y-2">
            {mine.map((l) => (
              <LeaveCard key={l.id} l={l} showWho={false}
                footer={
                  (l.status === 'Pending' || l.status === 'Approved') ? (
                    <button onClick={() => { if (confirm('Withdraw this leave application?')) cancel.mutate(l.id); }}
                      disabled={cancel.isPending}
                      className="px-3 py-1.5 text-xs border border-slate-300 rounded-lg text-slate-600 hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1">
                      <Ban size={12} /> Withdraw
                    </button>
                  ) : null
                }
              />
            ))}
          </div>
        )
      )}

      {tab === 'approvals' && (
        approvals.length === 0 ? <Empty text="Nothing is waiting on you." /> : (
          <div className="space-y-2">
            {approvals.map((l) => (
              <LeaveCard key={l.id} l={l} showWho
                footer={
                  <div className="flex items-center gap-2">
                    <button onClick={() => decide.mutate({ id: l.id, action: 'approve', remarks: '' })}
                      disabled={decide.isPending}
                      className="px-3 py-1.5 text-xs bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1">
                      <Check size={12} /> Approve
                    </button>
                    <button
                      onClick={() => {
                        // Required by the server too — asked for here so the reject doesn't bounce.
                        const remarks = prompt('Why is this leave being rejected?')?.trim();
                        if (!remarks) { toast.error('A reason is required to reject a leave.'); return; }
                        decide.mutate({ id: l.id, action: 'reject', remarks });
                      }}
                      disabled={decide.isPending}
                      className="px-3 py-1.5 text-xs border border-rose-300 text-rose-700 rounded-lg hover:bg-rose-50 disabled:opacity-50 flex items-center gap-1">
                      <X size={12} /> Reject
                    </button>
                  </div>
                }
              />
            ))}
          </div>
        )
      )}

      {formOpen && (
        <ApplyForm
          types={link?.leaveTypes ?? ['Casual', 'Sick', 'Earned', 'Unpaid']}
          onClose={() => setFormOpen(false)}
          onSaved={() => { setFormOpen(false); refresh(); }}
        />
      )}
    </div>
  );
}

function TabBtn({ active, onClick, label, count, highlight }: { active: boolean; onClick: () => void; label: string; count: number; highlight?: boolean }) {
  return (
    <button onClick={onClick}
      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-1.5 ${active ? 'border-primary text-primary' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
      {label}
      {count > 0 && (
        <span className={`text-[11px] px-1.5 py-0.5 rounded-full ${highlight ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>{count}</span>
      )}
    </button>
  );
}

function Loading() {
  return <div className="py-12 text-center text-slate-400"><Loader2 className="animate-spin inline" size={20} /></div>;
}
function Empty({ text }: { text: string }) {
  return (
    <div className="py-12 text-center text-slate-400">
      <Inbox size={28} className="mx-auto mb-2 opacity-50" />
      <p className="text-sm">{text}</p>
    </div>
  );
}

function LeaveCard({ l, showWho, footer }: { l: Leave; showWho: boolean; footer?: React.ReactNode }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3.5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-slate-800">{l.leaveType}</span>
            <span className={`text-[11px] px-1.5 py-0.5 rounded border ${STATUS_CHIP[l.status] ?? 'bg-slate-100 text-slate-600 border-slate-200'}`}>{l.status}</span>
            <span className="text-[11px] text-slate-500">{Number(l.days)} day{Number(l.days) === 1 ? '' : 's'}</span>
          </div>
          {showWho && (
            <div className="text-xs text-slate-600 mt-0.5">
              {l.employeeName ?? '—'}{l.empCode ? ` · ${l.empCode}` : ''}{l.department ? ` · ${l.department}` : ''}
            </div>
          )}
          <div className="text-sm text-slate-700 mt-1">{range(l)}</div>
          <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{l.reason}</p>
          {(l.handoverTo || l.contactDuringLeave) && (
            <div className="text-[11px] text-slate-500 mt-1">
              {l.handoverTo && <>Handover: {l.handoverTo}</>}
              {l.handoverTo && l.contactDuringLeave && ' · '}
              {l.contactDuringLeave && <>Contact: {l.contactDuringLeave}</>}
            </div>
          )}
          {l.status === 'Pending' && l.currentApproverName && (
            <div className="text-[11px] text-amber-700 mt-1">Awaiting {l.currentApproverName}</div>
          )}
          {(l.status === 'Approved' || l.status === 'Rejected') && (
            <div className="text-[11px] text-slate-500 mt-1">
              {l.status} by {l.decidedByName ?? '—'}{l.decisionRemarks ? ` — ${l.decisionRemarks}` : ''}
            </div>
          )}
        </div>
        {footer && <div className="shrink-0">{footer}</div>}
      </div>
    </div>
  );
}

function ApplyForm({ types, onClose, onSaved }: { types: string[]; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    leaveType: types[0] ?? 'Casual',
    fromDate: todayIso(),
    toDate: todayIso(),
    isHalfDay: false,
    halfDaySession: 'First',
    reason: '',
    handoverTo: '',
    contactDuringLeave: '',
  });
  const [err, setErr] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => (await api.post('/me/leave', form)).data,
    onSuccess: () => { toast.success('Leave applied — sent for approval'); onSaved(); },
    onError: (e: any) => setErr(e?.response?.data?.message ?? 'Could not submit the leave'),
  });

  // Mirrors the server's count so the employee sees the number before they commit to it.
  const days = useMemo(() => {
    if (form.isHalfDay) return 0.5;
    const f = new Date(`${form.fromDate}T00:00:00Z`);
    const t = new Date(`${form.toDate}T00:00:00Z`);
    if (isNaN(f.getTime()) || isNaN(t.getTime()) || t < f) return 0;
    let n = 0;
    const cur = new Date(f.getTime());
    while (cur <= t) { if (cur.getUTCDay() !== 0) n++; cur.setUTCDate(cur.getUTCDate() + 1); }
    return n;
  }, [form.fromDate, form.toDate, form.isHalfDay]);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-start justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-xl w-full max-w-lg mt-10 shadow-xl">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h2 className="font-semibold">Apply for Leave</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3">
          {err && <div className="text-sm bg-rose-50 border border-rose-200 text-rose-700 rounded-lg p-2.5">{err}</div>}

          <label className="block">
            <span className="text-xs font-medium text-slate-600">Leave Type</span>
            <select value={form.leaveType} onChange={(e) => setForm({ ...form, leaveType: e.target.value })}
              className="w-full border rounded px-2 py-1.5 text-sm mt-1">
              {types.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-medium text-slate-600">From</span>
              <input type="date" value={form.fromDate}
                onChange={(e) => {
                  const v = e.target.value;
                  // Keep To at or after From — a backwards range is refused by the server anyway.
                  setForm((f) => ({ ...f, fromDate: v, toDate: f.isHalfDay || f.toDate < v ? v : f.toDate }));
                }}
                className="w-full border rounded px-2 py-1.5 text-sm mt-1" />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-600">To</span>
              <input type="date" value={form.toDate} min={form.fromDate} disabled={form.isHalfDay}
                onChange={(e) => setForm({ ...form, toDate: e.target.value })}
                className="w-full border rounded px-2 py-1.5 text-sm mt-1 disabled:bg-slate-100" />
            </label>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <label className="flex items-center gap-1.5 text-sm text-slate-700">
              <input type="checkbox" checked={form.isHalfDay}
                onChange={(e) => setForm({ ...form, isHalfDay: e.target.checked, toDate: e.target.checked ? form.fromDate : form.toDate })} />
              Half day
            </label>
            {form.isHalfDay && (
              <select value={form.halfDaySession} onChange={(e) => setForm({ ...form, halfDaySession: e.target.value })}
                className="border rounded px-2 py-1 text-sm">
                <option value="First">First half</option>
                <option value="Second">Second half</option>
              </select>
            )}
            <span className="text-xs text-slate-500 ml-auto">
              {days > 0 ? <>Counts as <b className="text-slate-700">{days}</b> working day{days === 1 ? '' : 's'} (Sundays excluded)</> : 'No working days in that range'}
            </span>
          </div>

          <label className="block">
            <span className="text-xs font-medium text-slate-600">Reason *</span>
            <textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} rows={3}
              placeholder="Why do you need this leave?" className="w-full border rounded px-2 py-1.5 text-sm mt-1" />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-medium text-slate-600">Handover To</span>
              <input value={form.handoverTo} onChange={(e) => setForm({ ...form, handoverTo: e.target.value })}
                placeholder="Who covers your work" className="w-full border rounded px-2 py-1.5 text-sm mt-1" />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-600">Contact While Away</span>
              <input value={form.contactDuringLeave} onChange={(e) => setForm({ ...form, contactDuringLeave: e.target.value })}
                placeholder="Mobile" className="w-full border rounded px-2 py-1.5 text-sm mt-1" />
            </label>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t bg-slate-50 rounded-b-xl">
          <button onClick={onClose} className="px-3 py-2 text-sm text-slate-600 hover:text-slate-900">Cancel</button>
          <button onClick={() => { setErr(null); save.mutate(); }}
            disabled={save.isPending || !form.reason.trim() || days <= 0}
            className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-1.5">
            {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Submit
          </button>
        </div>
      </div>
    </div>
  );
}
